/**
 * useLandingTracking.js
 *
 * Call in LandingPage, passing the current product slug/id.
 * Re-fires a visit ping and restarts the heartbeat whenever the product changes,
 * so the admin dashboard shows accurate per-product live visitor counts.
 *
 * Usage in LandingPage.jsx:
 *   import { useLandingTracking } from '../hooks/useLandingTracking';
 *
 *   const visitorId = useLandingTracking(id); // id from useParams()
 *   // then pass visitorId in createlead and sendMetaConversionsAPI payloads
 */

import { useEffect, useRef } from 'react';
import { API_BASE_URL } from '../config/constants';
import { getABVariants } from './useABTest';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const VISITOR_KEY = '_lv_visitor_id';

const generateUUID = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

/**
 * Get or create a persistent visitor_id stored in localStorage.
 * Exported so lead/order request builders can read it without calling the hook.
 */
export const getOrCreateVisitorId = () => {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = generateUUID();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    // localStorage blocked (private browsing on some browsers)
    return generateUUID();
  }
};

const getQueryParam = (name) =>
  new URLSearchParams(window.location.search).get(name);

const getCookieValue = (name) => {
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)')
  );
  return match ? decodeURIComponent(match[1]) : null;
};

const setCookieValue = (name, value, days = 180) => {
  try {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  } catch {}
};

/**
 * Get or create a _fbp value.
 * Meta format: fb.1.<creation-timestamp-ms>.<random-uint32>
 * Priority: _fbp cookie (set by pixel) → localStorage fallback → self-generate & persist.
 * Self-generated values are valid for Meta Conversions API server-side matching.
 */
const getOrCreateFbp = () => {
  // 1. Cookie set by the pixel (best)
  const fromCookie = getCookieValue('_fbp');
  if (fromCookie) return fromCookie;

  // 2. Previously self-generated, stored in localStorage
  try {
    const fromStorage = localStorage.getItem('_fbp_self');
    if (fromStorage) return fromStorage;
  } catch {}

  // 3. Generate a new one and persist it
  const fbp = `fb.1.${Date.now()}.${Math.floor(Math.random() * 4294967296)}`;
  try {
    localStorage.setItem('_fbp_self', fbp);
    setCookieValue('_fbp', fbp, 180); // also set as cookie so pixel picks it up
  } catch {}
  return fbp;
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * @param {string|null} productSlug  The current product id/slug from useParams().
 *   Pass it so the hook re-fires a visit ping and restarts the heartbeat whenever
 *   the user navigates to a different product page without a full page reload.
 * @returns {string} The stable visitor_id for this browser session.
 */
const useLandingTracking = (productSlug = null) => {
  const visitorId  = useRef(getOrCreateVisitorId());
  const visitIdRef = useRef(null); // DB row id returned by POST /landing/visit

  useEffect(() => {
    if (!productSlug) return;

    let cancelled  = false;
    let heartbeat  = null;

    // Delay ping by 1500ms so the Facebook Pixel has time to load and
    // set the _fbp cookie before we read it. For returning visitors the
    // cookie already exists so the delay has no effect on data quality.
    const timer = setTimeout(async () => {
      if (cancelled) return;

      const payload = {
        visitor_id:   visitorId.current,
        page_url:     window.location.href,
        product_slug: productSlug,
        utm_source:    getQueryParam('utm_source'),
        utm_campaign:  getQueryParam('utm_campaign'),
        utm_medium:    getQueryParam('utm_medium'),
        utm_content:   getQueryParam('utm_content'),
        utm_id:        getQueryParam('utm_id'),
        utm_term:      getQueryParam('utm_term'),
        fbclid:        getQueryParam('fbclid'),
        fbp:           getOrCreateFbp(),
        // _fbc may have been stored by captureAndStoreFbclid() in fbclid.js
        fbc:           getCookieValue('_fbc') || localStorage.getItem('_fbc_fallback'),
        ab_variants:   getABVariants(),
      };

      try {
        const res = await fetch(`${API_BASE_URL}/public/landing/visit`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify(payload),
        });
        if (!cancelled && res.ok) {
          const json = await res.json();
          // Store the DB row id so the heartbeat targets the exact row for
          // this product — prevents product A's row being kept "live" after
          // the user switches to product B.
          if (json?.data?.id) visitIdRef.current = json.data.id;
        }
      } catch (err) {
        console.warn('[LandingTracking] visit ping failed (non-blocking):', err);
      }

      if (cancelled) return;

      // ── Heartbeat ────────────────────────────────────────────────────────
      // Pings every 30 s to keep last_seen_at fresh in the DB.
      // Uses the specific visit row id so switching products correctly moves
      // the "live" indicator from the old product to the new one.
      heartbeat = setInterval(() => {
        const id = visitIdRef.current;
        if (!id) return;
        fetch(`${API_BASE_URL}/public/landing/ping`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ visit_id: id }),
        }).catch(() => {}); // fully non-blocking
      }, 30_000);
    }, 1500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (heartbeat) clearInterval(heartbeat);
      visitIdRef.current = null; // reset so next product gets its own visitId
    };
  }, [productSlug]); // re-run when user navigates to a different product

  return visitorId.current;
};

export default useLandingTracking;
